/**
 * QMP-FE-4 — STEP 4 "NCRs & RFIs": 4a NCR rules · 4b RFI rules · 4c Who else
 * can see them.
 *
 * LAYOUT, COPY AND VALUES ARE THE MOCKUP'S (`mockup-qmp-v79-official.html`,
 * `#w4`, the `rt()` renderer): one subarea at a time (D11.13), each a first
 * row "All packages" / "All other packages" plus the packages with a row of
 * their own ("own rule" / "automatic · <reason>"), drawn as `table.endt.rt`.
 * The grammar lives in `lib/qmpRules`; the state in `useQmpRuleEditor`, held
 * by the page; the table, card and subnav are step 2's (`ItpRulesStep`).
 *
 * NO BUSINESS RULE IS REPEATED HERE beyond the grammar of the rows: the server
 * decides, and its refusal is shown on the row of the package it names.
 */
import React from 'react';
import {
  Subnav, SubCard, LoadError, useTip, ValueSelect, CONTROL_ENDT, RuleTable, RuleFoot, QButton,
} from './ItpRulesStep';
import {
  formatResponseTime, canVaryByCompany, setPerCompany, forcedNull, fieldOptions,
  DUE_POSITION, POSITION_LABEL,
} from '../../lib/qmpRules';

// QMP-FE-7 — the titles are the server's (`editor.titles`).
export const SUBS4 = ['ncr', 'rfi', 'visibility'];

// ---------------------------------------------------------------------------
// mockup tokens (`.chip`, `.chip.lock`, `.chip.dimc`, `.setbelow`, `.splitlnk`)
// ---------------------------------------------------------------------------
const CHIP = 'inline-flex items-center gap-[6px] text-[10px] font-semibold px-[8px] py-[3px] '
  + 'rounded-full whitespace-nowrap mt-[5px]';
const CHIP_LOCK = `${CHIP} text-[#94a3b8] border border-[#1c2941] bg-[#0b1322]`;
const CHIP_DIM = `${CHIP} text-[#5b6b85] border border-[#16223a] bg-transparent`;
const SetBelow = () => <span className="text-[#94a3b8] text-[12px] italic">Set per company below</span>;
const Na = () => <span className={CHIP_DIM}>N/A</span>;
const DUE_KEYS = ['executing', 'verification', 'acceptance'];
// `.seclbl` (v79:180) and `.vpnote` (v79:263).
const SECLBL = 'text-[10.5px] tracking-[.1em] uppercase text-[#5b6b85] font-bold mb-[10px]';
const VPNOTE = 'bg-[#0d1526] border border-[#16223a] rounded-[10px] px-[14px] py-[11px] text-[#94a3b8] text-[12.5px] mb-[12px]';

// ---------------------------------------------------------------------------
// tooltips (mockup `.tip` bodies)
// ---------------------------------------------------------------------------
const TIPS4 = {
  ncrResp: {
    title: 'Response due',
    body: (
      <>
        The time the company that must fix the non-conformance has to submit its proposed fix —
        root cause and corrective action — counted from when the NCR is raised. A late response is
        recorded against that company. An NCR never closes because a deadline has passed. Where
        the companies follow different contracts, choose &quot;Varies between companies&quot; and
        set a time for each company.
      </>
    ),
  },
  ncrDec: {
    title: 'Decision due',
    body: (
      <>
        The time each company chosen to accept the NCR has to approve or reject what is submitted —
        the proposed fix, and later the close-out — counted from when it is submitted. If one
        rejects the fix, the NCR stays open and a new version is proposed. A late decision is
        recorded against that company. The deadline never accepts a fix by itself.
      </>
    ),
  },
  visAp: {
    title: 'Acceptance Party visibility',
    body: (
      <>
        NCRs and RFIs between the company doing the work and the company checking it do not name
        the Acceptance Party. Choose whether the Acceptance Party can read them anyway. Records
        that name the Acceptance Party are always visible to it.
      </>
    ),
  },
  visEp: {
    title: 'Executing Party visibility',
    body: (
      <>
        NCRs and RFIs between the company checking the work and the company accepting it do not
        name the Executing Party. Choose whether the Executing Party can read them anyway. A
        record that names the Executing Party — for example an NCR on its work — is always
        visible to it.
      </>
    ),
  },
  rfiDue: {
    title: 'Answer due',
    body: (
      <>
        How long the company that received the RFI has to answer. ConstructQA sends reminders and
        records an overdue answer if the deadline is missed. <b>Time never closes an RFI
        automatically.</b>
      </>
    ),
  },
};

// ---------------------------------------------------------------------------
// cells
// ---------------------------------------------------------------------------

/** Mockup `partyLbl`: the first row names the position; a package, the
 *  company (or "To be appointed"); null where the package has no such party. */
export function partyLabel(row, key) {
  const pos = DUE_POSITION[key];
  if (!row) return POSITION_LABEL[pos];
  if (pos === 'execution') {
    return row.executing && row.executing.appointed !== false ? row.executing.name : 'To be appointed';
  }
  return row[pos] ? row[pos].name : null;
}

// Mockup `isSplit`: set per company, and the package can be.
const isSplit = (value, row) => Boolean(value && value.per_company) && canVaryByCompany(row);

// The subarea each due field belongs to, for its served choices.
const DUE_SECTION = { response_due: 'ncr', decision_due: 'ncr', answer_due: 'rfi' };

// One deadline (`gsel(CLOCK, ...)`) of a due field, "all" or one company —
// the served choices of that field.
function DueSelect({
  value, field, dueKey, onChange, disabled, testId, options,
}) {
  const dues = (value && value[field]) || {};
  return (
    <ValueSelect
      served={fieldOptions(options, DUE_SECTION[field], field)}
      current={dues[dueKey]}
      fallback={formatResponseTime}
      disabled={disabled}
      className={CONTROL_ENDT}
      onPick={(v) => onChange({ ...value, [field]: { ...dues, [dueKey]: v } })}
      testId={testId}
    />
  );
}

// Mockup `splitLnk2`: "Varies between companies" / "Same for all companies",
// only where the package has two companies.
function SplitLink({
  section, value, row, onChange, disabled, k,
}) {
  if (!canVaryByCompany(row) || forcedNull(section, row)) return null;
  const on = Boolean(value && value.per_company);
  return (
    <div className="text-[12px]">
      <button
        type="button"
        disabled={disabled}
        className="text-[12px] text-[#93c5fd] underline decoration-dotted hover:text-[#f97316] disabled:opacity-60 disabled:cursor-default"
        onClick={() => onChange(setPerCompany(section, value, row, !on))}
        data-testid={`qr-split-${section}-${k}`}
      >
        {on ? 'Same for all companies' : 'Varies between companies'}
      </button>
    </div>
  );
}

// 4a — D14.6 / D17.3: response due (the company that must fix it), decision due
// (the companies that accept the NCR); per company, the Acceptance Party never fixes.
function NcrRespCell({
  value, row, onChange, disabled, k, options,
}) {
  if (isSplit(value, row)) return <SetBelow />;
  return (
    <>
      <DueSelect options={options} value={value} field="response_due" dueKey="all" onChange={onChange} disabled={disabled} testId={`qr-ncr-resp-${k}`} />
      <span className={CHIP_LOCK}>Company that must fix it</span>
    </>
  );
}

function NcrDecCell({
  value, row, onChange, disabled, k, options,
}) {
  if (isSplit(value, row)) return <SetBelow />;
  return (
    <>
      <DueSelect options={options} value={value} field="decision_due" dueKey="all" onChange={onChange} disabled={disabled} testId={`qr-ncr-dec-${k}`} />
      <span className={CHIP_LOCK}>Companies that accept</span>
    </>
  );
}

const NcrLinkCell = (props) => <SplitLink section="ncr" {...props} />;

// Mockup `ncrSub`: one "↳" line per company the row has.
function ncrSubRows({
  value, row, k, onChange, disabled, options,
}) {
  if (!isSplit(value, row)) return [];
  return DUE_KEYS.map((key) => ({ key, label: partyLabel(row, key) }))
    .filter((p) => p.label !== null)
    .map(({ key, label }) => ({
      key,
      label,
      cells: [
        key === 'acceptance' ? <Na /> : (
          <DueSelect options={options} value={value} field="response_due" dueKey={key} onChange={onChange} disabled={disabled} testId={`qr-ncr-resp-${k}-${key}`} />
        ),
        <DueSelect options={options} value={value} field="decision_due" dueKey={key} onChange={onChange} disabled={disabled} testId={`qr-ncr-dec-${k}-${key}`} />,
        null,
      ],
    }));
}

// 4b — D14.7: RFIs follow the chain; internal QA has none (its value is null).
function RfiDueCell({
  value, row, onChange, disabled, k, options,
}) {
  if (forcedNull('rfi', row)) {
    return <span className={CHIP_DIM}>Internal QA — no other company to ask</span>;
  }
  if (isSplit(value, row)) return <SetBelow />;
  return (
    <DueSelect options={options} value={value} field="answer_due" dueKey="all" onChange={onChange} disabled={disabled} testId={`qr-rfi-due-${k}`} />
  );
}

const RfiLinkCell = (props) => <SplitLink section="rfi" {...props} />;

// Mockup `rfiSub`.
function rfiSubRows({
  value, row, k, onChange, disabled, options,
}) {
  if (forcedNull('rfi', row) || !isSplit(value, row)) return [];
  return DUE_KEYS.map((key) => ({ key, label: partyLabel(row, key) }))
    .filter((p) => p.label !== null)
    .map(({ key, label }) => ({
      key,
      label,
      cells: [
        <DueSelect options={options} value={value} field="answer_due" dueKey={key} onChange={onChange} disabled={disabled} testId={`qr-rfi-due-${k}-${key}`} />,
        null,
      ],
    }));
}

// 4c — D14.8: only the third company's view is a choice, and only with three
// parties (mockup `yn`); elsewhere the value is null.
const yesNoCell = (field) => function VisibilityCell({
  value, row, onChange, disabled, k, options,
}) {
  if (forcedNull('visibility', row)) {
    const internalQa = !row.verification && !row.acceptance;
    return (
      <span className={CHIP_DIM}>{internalQa ? 'Internal QA — Executing Party' : 'Only two companies'}</span>
    );
  }
  return (
    <ValueSelect
      served={fieldOptions(options, 'visibility', field)}
      current={Boolean(value && value[field])}
      fallback={String}
      disabled={disabled}
      className={CONTROL_ENDT}
      onPick={(v) => onChange({ ...value, [field]: v })}
      testId={`qr-vis-${field}-${k}`}
    />
  );
};

// The two tables of `#s4c` (`rt4c`, `rt4c2`): one state, two views of it.
const VIS_ACCEPTANCE = {
  look: 'endt',
  columns: [
    { key: 'ncr', label: 'See Executing–Verification NCRs?', width: '30%' },
    { key: 'rfi', label: 'See Executing–Verification RFIs?', width: '30%' },
  ],
  cells: [yesNoCell('acceptance_sees_ev_ncrs'), yesNoCell('acceptance_sees_ev_rfis')],
};
const VIS_EXECUTING = {
  look: 'endt',
  columns: [
    { key: 'ncr', label: 'See Verification–Acceptance NCRs?', width: '30%' },
    { key: 'rfi', label: 'See Verification–Acceptance RFIs?', width: '30%' },
  ],
  cells: [yesNoCell('executing_sees_va_ncrs'), yesNoCell('executing_sees_va_rfis')],
};

function VisibilityTables({ section, editor, openTip }) {
  return (
    <>
      <div className={SECLBL}>
        Acceptance Party visibility
        <QButton tipKey="visAp" onOpen={openTip} />
      </div>
      <RuleTable section={section} editor={editor} openTip={openTip} conf={VIS_ACCEPTANCE} />
      <div className={`${SECLBL} mt-[18px]`}>
        Executing Party visibility
        <QButton tipKey="visEp" onOpen={openTip} />
      </div>
      <RuleTable
        section={section}
        editor={editor}
        openTip={openTip}
        conf={VIS_EXECUTING}
        notes={false}
        tid="visibility-va"
      />
      <div className={`${SECLBL} mt-[18px]`}>Verification Party visibility</div>
      <div className={VPNOTE} data-testid="qr-vpnote">
        Sees every NCR and RFI in the package — it is part of both links of the chain
        (Executing–Verification and Verification–Acceptance). Nothing to set.
      </div>
      <RuleFoot section={section} editor={editor} conf={VIS_ACCEPTANCE} />
    </>
  );
}

// ---------------------------------------------------------------------------
// the subareas (mockup `#s4a`, `#s4b`, `#s4c`)
// ---------------------------------------------------------------------------
const SECTIONS4 = {
  ncr: {
    look: 'endt',
    title: 'Non-conformances (NCRs)',
    help: 'Set who must respond, who decides, and by when.',
    columns: [
      { key: 'resp', label: 'Response due', width: '26%', tip: 'ncrResp' },
      { key: 'dec', label: 'Decision due', width: '26%', tip: 'ncrDec' },
      { key: 'lnk', label: '', width: '18%' },
    ],
    cells: [NcrRespCell, NcrDecCell, NcrLinkCell],
    subRows: ncrSubRows,
    how: (
      <>
        Any company can raise an NCR, including against its own work. The company that must fix
        the issue proposes a fix; <b>every company chosen to accept the NCR approves or rejects
        it — the fix and, later, the close-out.</b> If one rejects the fix, the NCR stays open and a
        new version is proposed. A company correcting its own work is never the only one accepting
        while another company is in the package.
        Deadlines remind and record overdue — they never close, accept or approve anything by
        themselves. You can set one time for everyone, or a different time for each company —
        Executing, Verification, Acceptance. The Acceptance Party never fixes work, so its response
        time is N/A; a package that does not have one of those parties simply does not show it.
      </>
    ),
  },
  rfi: {
    look: 'endt',
    title: 'Requests for information (RFIs)',
    help: 'Set how long the company that receives an RFI has to answer it.',
    columns: [
      { key: 'due', label: 'Answer due', width: '32%', tip: 'rfiDue' },
      { key: 'lnk', label: '', width: '24%' },
    ],
    cells: [RfiDueCell, RfiLinkCell],
    subRows: rfiSubRows,
    how: (
      <>
        An RFI is a question from one company to the company above or below it in the package
        chain; whoever receives it answers within the time set here. You can set one time for
        everyone, or a different time for each company — Executing, Verification, Acceptance. A
        package that does not have one of those parties simply does not show it. A package with one
        company has no RFIs between companies. Deadlines remind and record overdue — they never
        answer or close an RFI by themselves.
      </>
    ),
  },
  visibility: {
    look: 'endt',
    title: 'Visibility of NCRs and RFIs',
    help: 'Decide whether the third company in the chain can see NCRs and RFIs it is not part of.',
    render: (props) => <VisibilityTables {...props} />,
    how: (
      <>
        Companies always see the records they are directly involved in. Use these rules only to
        decide whether the <b>third company in the quality chain</b> can also see those NCRs or
        RFIs.
      </>
    ),
  },
};

// QMP-FE-8b — `subs`: the tabs of the v83 page this step sits on (NCRs, RFIs,
// visibility and AI); the cards are still this step's three.
export default function NcrRfiStep({
  editor, sub, onSub, subs = SUBS4,
}) {
  const { open, pop } = useTip(TIPS4);
  return (
    <div
      className="bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[26px] py-[24px] min-w-0"
      data-testid="qs-pane-4"
    >
      <Subnav sub={sub} onSub={onSub} status={editor.status} subs={subs} titles={editor.titles} />
      <LoadError editor={editor} />
      <SubCard key={sub} section={sub} conf={SECTIONS4[sub]} editor={editor} openTip={open} />
      {pop}
    </div>
  );
}
